import type { PagedListBaseParams } from '@/hooks/usePagedListParams'

/** Predefined office-expense categories. Custom categories are free-form strings on top of these. */
export const PREDEFINED_EXPENSE_CATEGORIES = [
  'furniture',
  'tea_coffee',
  'water',
  'rent',
  'utilities',
  'cleaning_maintenance',
  'office_supplies',
  'meals_entertainment',
  'other',
] as const

export type PredefinedExpenseCategory = (typeof PREDEFINED_EXPENSE_CATEGORIES)[number]

/** Category is a string so organizations can add custom categories beyond the predefined set. */
export type ExpenseCategory = PredefinedExpenseCategory | (string & {})

export const PAYMENT_METHODS = ['cash', 'card', 'bank_transfer', 'cheque', 'other'] as const
export type PaymentMethod = (typeof PAYMENT_METHODS)[number]

/** Reference to an uploaded receipt/invoice (subset of the /upload document shape). */
export interface ExpenseAttachment {
  id: string
  url: string
  originalName: string
  mimeType: string
  size: number
}

export interface ExpenseEntry {
  _id: string
  organizationId: string
  /** Absent (or null) for an organization expense: no company carries it. */
  companyId?: string | null
  title: string
  category: ExpenseCategory
  amount: number
  currency: string
  /** ISO date string (date of expense). */
  date: string
  description?: string
  paymentMethod: PaymentMethod
  attachments?: ExpenseAttachment[]
  createdBy?: string
  createdAt: string
  updatedAt: string
}

export interface CreateExpenseDto {
  title: string
  category: ExpenseCategory
  amount: number
  currency?: string
  date: string
  description?: string
  paymentMethod: PaymentMethod
  attachments?: ExpenseAttachment[]
}

export type UpdateExpenseDto = Partial<CreateExpenseDto>

/** Company-scoped list query (GET /companies/:id/expenses). */
export interface ExpenseListQuery extends PagedListBaseParams {
  category?: ExpenseCategory
  paymentMethod?: PaymentMethod
  dateFrom?: string
  dateTo?: string
  amountMin?: number
  amountMax?: number
}

/**
 * Which expenses a workspace query covers: all of them, only the
 * organization's own (no company), or only company expenses.
 */
export type ExpenseScopeType = 'all' | 'organization' | 'company'

/** Cross-company workspace query (GET /expenses). */
export interface ExpenseWorkspaceQuery {
  companyIds?: string[]
  scope?: ExpenseScopeType
  category?: ExpenseCategory
  paymentMethod?: PaymentMethod
  dateFrom?: string
  dateTo?: string
  amountMin?: number
  amountMax?: number
  search?: string
  page?: number
  limit?: number
}

/** Response shape for GET /expenses (workspace). */
export interface ExpenseWorkspaceResponse {
  data: ExpenseEntry[]
  pagination: { total: number; page: number; limit: number }
}

/** A custom category record returned by GET /companies/:id/expenses/categories. */
export interface ExpenseCategoryRecord {
  _id: string
  companyId?: string
  name: string
  createdAt: string
}

export interface ManagedExpenseCategory {
  id: string
  name: string
  usageCount: number
}

// ─── Analytics ───────────────────────────────────────
export type AnalyticsPeriodMode = 'range' | 'week' | 'month' | 'year'

export interface ExpenseAnalyticsQuery {
  companyIds?: string[]
  scope?: ExpenseScopeType
  mode: AnalyticsPeriodMode
  /** ISO date strings bounding the period (always sent; derived from mode on the client). */
  dateFrom: string
  dateTo: string
}

export interface CategoryTotal {
  category: ExpenseCategory
  total: number
}

export interface CompanyTotal {
  /** `null` is the organization's own expenses. */
  companyId: string | null
  total: number
}

export type TrendBucket = 'day' | 'month'

export interface TrendPoint {
  /** Bucket label, e.g. '2026-06' or '2026-W24' or '2026-06-15'. */
  period: string
  total: number
}

export interface ExpenseAnalytics {
  total: number
  currency: string
  byCategory: CategoryTotal[]
  byCompany: CompanyTotal[]
  trend: TrendPoint[]
  /** What one `trend` period is: `day` → 'YYYY-MM-DD', `month` → 'YYYY-MM'. */
  trendBucket: TrendBucket
  /** `trend` split by category, so each period can show what it was spent on. */
  trendByCategory: { period: string; category: ExpenseCategory; total: number }[]
  topCategories: CategoryTotal[]
}
